import type React from "react";
import { useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { EventDateSection } from "@/components/common/EventDateSection";
import { RecurrenceEditor } from "@/components/common/RecurrenceEditor";
import { TimeZoneFieldWithModal } from "@/components/common/TimeZoneFieldWithModal";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { EVENT_TITLE_MAX_LENGTH, LANG_EN, LANG_JP } from "@/constants";
import { StepFooter } from "@/features/create-event/components/StepFooter";
import type { StepProps } from "@/features/create-event/types";
import { convertTimezoneToTimezone } from "@/lib/utils/dateUtils";
import {
  buildSortedTimeZoneOptions,
  getCurrentBrowserTimeZone,
} from "@/lib/utils/timeZone";

export const StepEventDetails: React.FC<StepProps> = ({
  formData,
  onChange,
  handleNext,
  handleSaveDraft,
  setStep,
  isSubmitting,
  recurrenceErrors,
}) => {
  const { t } = useTranslation();
  const eventTitleId = useId();
  const languageId = useId();
  const recurrenceToggleId = useId();
  const browserTimeZone = useMemo(() => getCurrentBrowserTimeZone(), []);
  const selectedTimeZone = formData.timeZone || browserTimeZone || "UTC";
  const timeZoneOptions = useMemo(
    () => buildSortedTimeZoneOptions(selectedTimeZone),
    [selectedTimeZone],
  );

  const handleTimeZoneChange = (nextTimeZone: string) => {
    if (nextTimeZone === selectedTimeZone) return;
    if (!formData.startTime || !formData.endTime) {
      onChange({ timeZone: nextTimeZone });
      return;
    }
    const nextStart = convertTimezoneToTimezone(
      formData.startDate,
      formData.startTime,
      selectedTimeZone,
      nextTimeZone,
    );
    const nextEnd = convertTimezoneToTimezone(
      formData.endDate,
      formData.endTime,
      selectedTimeZone,
      nextTimeZone,
    );

    onChange({
      timeZone: nextTimeZone,
      startDate: nextStart.date,
      startTime: nextStart.time,
      endDate: nextEnd.date,
      endTime: nextEnd.time,
    });
  };

  const isSaveDraftDisabled = !formData.title;
  const isNextDisabled = formData.isAllDay
    ? !formData.title || !formData.startDate || !formData.endDate
    : !formData.title ||
      !formData.startDate ||
      !formData.startTime ||
      !formData.endDate ||
      !formData.endTime;

  return (
    <div className="flex h-full flex-col gap-6 p-6">
      <h2 className="text-xl font-semibold text-rw-black">
        {t("eventManage.aboutEvent")}
      </h2>

      <div className="space-y-4">
        <div className="space-y-1">
          <div className="flex justify-between">
            <label htmlFor={eventTitleId} className="block text-sm font-medium">
              {t("eventCreate.eventName")}*
            </label>
            <span className="text-xs text-gray-500">
              {formData.title.length}/{EVENT_TITLE_MAX_LENGTH}
            </span>
          </div>
          <input
            id={eventTitleId}
            type="text"
            placeholder={`${t("eventCreate.eventName")}*`}
            value={formData.title || ""}
            onChange={(e) => onChange({ ...formData, title: e.target.value })}
            className="w-full rounded border p-2"
            maxLength={EVENT_TITLE_MAX_LENGTH}
          />
        </div>

        <div className="space-y-1">
          <label htmlFor={languageId} className="block text-sm font-medium">
            {t("common.language")}*
          </label>
          <select
            id={languageId}
            value={formData.language}
            onChange={(e) =>
              onChange({ ...formData, language: e.target.value })
            }
            className="w-full rounded border p-2"
          >
            <option value={LANG_EN}>{t("common.english")}</option>
            <option value={LANG_JP}>{t("common.japanese")}</option>
          </select>
        </div>

        <TimeZoneFieldWithModal
          value={selectedTimeZone}
          options={timeZoneOptions}
          onChange={handleTimeZoneChange}
          className="space-y-1"
        />

        <EventDateSection
          eventData={formData}
          onChange={(data) => onChange({ ...data })}
          className="text-rw-black-gray"
        />

        <div className="flex items-center space-x-2">
          <Switch
            id={recurrenceToggleId}
            checked={Boolean(formData.recurrence)}
            onCheckedChange={(checked) => {
              if (checked) {
                const defaultUntil =
                  formData.endDate || formData.startDate || "";
                onChange({
                  ...formData,
                  recurrence: formData.recurrence ?? {
                    freq: "DAILY",
                    interval: 1,
                    until: defaultUntil,
                  },
                });
              } else {
                onChange({ ...formData, recurrence: null });
              }
            }}
          />
          <Label htmlFor={recurrenceToggleId} className="text-sm font-medium">
            {t("common.enableRecurrence", "Enable recurrence")}
          </Label>
        </div>

        {formData.recurrence && (
          <RecurrenceEditor
            value={formData.recurrence}
            startDate={formData.startDate}
            endDate={formData.endDate}
            onChange={(rule) => onChange({ ...formData, recurrence: rule })}
            errors={recurrenceErrors}
          />
        )}
      </div>

      <StepFooter
        isSubmitting={isSubmitting}
        onBack={() => setStep?.((prev) => Math.max(1, prev - 1))}
        onSaveDraft={handleSaveDraft}
        onNext={handleNext}
        nextDisabled={isNextDisabled}
        saveDraftDisabled={isSaveDraftDisabled}
      />
    </div>
  );
};
